import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { PageHeader, Modal, inputCls, btnPrimary, EmptyState } from "../../components/bits";
import { UploadCloud, Trash2, ImageOff } from "lucide-react";

export default function Gallery() {
  const [items, setItems] = useState(null);
  const [opps, setOpps] = useState([]);
  const [modal, setModal] = useState(false);
  const [files, setFiles] = useState([]);
  const [oppId, setOppId] = useState("");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/portal/gallery").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => {
    load();
    api.get("/portal/opportunities").then((r) => setOpps(r.data)).catch(() => {});
  }, []);

  const readFiles = (list) => {
    const arr = Array.from(list).slice(0, 5);
    if (Array.from(list).length > 5) toast.error("الحد الأقصى 5 صور في الدفعة الواحدة");
    Promise.all(arr.map((f) => new Promise((res, rej) => {
      if (!f.type.startsWith("image/")) return rej();
      if (f.size > 2_500_000) return rej();
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(f);
    }))).then((imgs) => setFiles((prev) => [...prev, ...imgs].slice(0, 5)))
      .catch(() => toast.error("تأكد أن الملفات صور وحجمها أقل من 2.5MB"));
  };

  const upload = async () => {
    if (!files.length) {
      toast.error("أرفق صورة واحدة على الأقل");
      return;
    }
    setBusy(true);
    try {
      await api.post("/portal/gallery", { opportunity_id: oppId, caption, images: files });
      toast.success(`تم رفع ${files.length} صورة لمعرض الأعمال`);
      setModal(false);
      setFiles([]);
      setCaption("");
      setOppId("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/portal/gallery/${id}`);
      toast.success("تم حذف الصورة");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!items) return <div className="text-center text-mutext py-20 font-head">جارٍ التحميل …</div>;

  return (
    <div data-testid="gallery-page">
      <PageHeader title="معرض الأعمال" sub="وثّق شواهد فرصك بالصور — حتى 5 صور دفعة واحدة بالسحب والإفلات">
        <button data-testid="gallery-upload-btn" onClick={() => setModal(true)} className={btnPrimary}>
          <UploadCloud className="w-4 h-4" /> رفع صور
        </button>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState testid="gallery-empty" text="المعرض فارغ — وثّق أول فرصة بالصور لتظهر في معرض الأثر العام" />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`gallery-item-${it.id.slice(0, 8)}`}
              className="relative rounded-2xl overflow-hidden group aspect-square border border-brd shadow-md">
              <img src={it.image} alt={it.opportunity_name || "صورة تطوعية"} className="w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-white">
                <div className="text-[11px] font-bold">{it.opportunity_name || "بدون ربط بفرصة"}</div>
                {it.caption && <div className="text-[10px] opacity-80 line-clamp-1">{it.caption}</div>}
              </div>
              <button data-testid={`gallery-delete-${it.id.slice(0, 8)}`} onClick={() => remove(it.id)}
                className="absolute top-2 left-2 w-7 h-7 rounded-lg bg-black/50 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="رفع صور معرض الأعمال" testid="gallery-upload-modal">
        <div className="space-y-4">
          <div data-testid="gallery-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); readFiles(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
              drag ? "border-coral-600 bg-coral-100 dark:bg-surface2" : "border-brd hover:border-turq-600"}`}>
            <UploadCloud className="w-8 h-8 mx-auto text-turq-600 mb-2" />
            <div className="text-sm font-bold">اسحب الصور هنا أو انقر للاختيار</div>
            <div className="text-[11px] text-mutext mt-1">حتى 5 صور · كل صورة أقل من 2.5MB</div>
            <input ref={fileRef} data-testid="gallery-file-input" type="file" accept="image/*" multiple hidden
              onChange={(e) => readFiles(e.target.files)} />
          </div>
          {files.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {files.map((f, i) => (
                <div key={f} className="relative w-16 h-16 rounded-xl overflow-hidden border border-brd">
                  <img src={f} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setFiles(files.filter((_, j) => j !== i))}
                    className="absolute inset-0 bg-black/40 text-white opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                    <ImageOff className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">ربط بفرصة تطوعية (اختياري)</label>
            <select data-testid="gallery-opp-select" value={oppId} onChange={(e) => setOppId(e.target.value)} className={inputCls}>
              <option value="">— بدون ربط —</option>
              {opps.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">وصف موجز (قصة الأثر)</label>
            <input data-testid="gallery-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} className={inputCls} />
          </div>
          <button data-testid="gallery-submit-btn" onClick={upload} disabled={busy} className={`${btnPrimary} w-full justify-center`}>
            {busy ? "جارٍ الرفع …" : `رفع ${files.length || ""} صورة`}
          </button>
        </div>
      </Modal>
    </div>
  );
}
